/**
 * Emissions Asset Dashboard Page
 *
 * Per-asset dashboard. Renders a header band (title, type, metadata,
 * Edit/Delete actions) above a TabNav (Overview / Coverage / Activities
 * / Targets / Documents). The Overview tab embeds `AssetDetailSection`
 * which owns the KPI strip, intensity row, trend chart, and breakdown
 * donuts. Coverage will host the sources × months data-quality grid in
 * a follow-up.
 */

import { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  BarChart3,
  Download,
  FileText,
  Loader2,
  Pencil,
  ShieldCheck,
  Target,
  Trash2,
} from 'lucide-react';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { TabNav, type TabItem } from '@g17eco/ui-modern/components/ui/tab-nav';
import { generateUrl } from '@routes/util';
import { emissionsTrackerRoutes } from '../routes';
import {
  useGetAssetQuery,
  useGetAssetComparisonQuery,
  useCreateActivityMutation,
} from '../api/emissions';
import { useEmissionsFilters } from '../hooks/useEmissionsFilters';
import { FiscalYearSelector, getFiscalYearPeriod } from '../components/FiscalYearSelector';
import { AssetDetailSection } from '../components/insights/AssetDetailSection';
import { AssetHeader } from '../components/assets/AssetHeader';
import { EditAssetModal } from '../components/assets/EditAssetModal';
import { DeleteAssetModal } from '../components/assets/DeleteAssetModal';
import { AssetOnePagerModal } from '../components/assets/AssetOnePagerModal';
import { AssetDataQualityTab } from '../components/assets/data-quality/AssetDataQualityTab';
import { AddActivityToExistingAssetButton } from '../components/form/AddActivityToExistingAssetButton';
import type { AssetActivityEntry } from '../components/form/types';
import { expandMonthToPeriodRange } from '../utils/activityUtils';
import {
  type ActivityUnit,
  type CreateActivityRequest,
  type FuelType,
  type ProcessType,
  type RefrigerantGasType,
  type VehicleType,
} from '../types/emissions';

type AssetSubtab = 'overview' | 'data-quality' | 'activities' | 'targets' | 'documents';

const SUBTAB_KEYS: Set<AssetSubtab> = new Set(['overview', 'data-quality', 'activities', 'targets', 'documents']);

const subtabs: TabItem<AssetSubtab>[] = [
  { key: 'overview', label: 'Overview', icon: <BarChart3 className='tw:size-4' /> },
  { key: 'data-quality', label: 'Data Quality', icon: <ShieldCheck className='tw:size-4' /> },
  { key: 'activities', label: 'Activities', icon: <Activity className='tw:size-4' />, disabled: true },
  { key: 'targets', label: 'Targets', icon: <Target className='tw:size-4' />, disabled: true },
  { key: 'documents', label: 'Documents', icon: <FileText className='tw:size-4' />, disabled: true },
];

const COMING_SOON_SUBTABS: Set<AssetSubtab> = new Set(
  subtabs.filter((t) => t.disabled).map((t) => t.key),
);

const getSubtabFromSearch = (search: string): AssetSubtab => {
  const subtab = new URLSearchParams(search).get('subtab');
  return subtab && SUBTAB_KEYS.has(subtab as AssetSubtab) ? (subtab as AssetSubtab) : 'overview';
};

export const EmissionsAssetDashboardPage = () => {
  const { initiativeId = '', assetId = '' } = useParams<{ initiativeId: string; assetId: string }>();
  const history = useHistory();
  const location = useLocation();
  const { selectedFiscalYear, setSelectedFiscalYear, availableYears } = useEmissionsFilters();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isOnePagerOpen, setIsOnePagerOpen] = useState(false);
  const [createActivity] = useCreateActivityMutation();

  const period = useMemo(() => getFiscalYearPeriod(selectedFiscalYear), [selectedFiscalYear]);

  // All-time bounds for the Trend pair. `availableYears` may be undefined while
  // the activity-years hook is in flight — render only when we have it.
  const allTimePeriod = useMemo(() => {
    if (!availableYears || availableYears.length === 0) return undefined;
    const minYear = Math.min(...availableYears);
    const maxYear = Math.max(...availableYears);
    return { start: `${minYear}-01-01`, end: `${maxYear}-12-31` };
  }, [availableYears]);
  const activeSubtab = getSubtabFromSearch(location.search);

  const setActiveSubtab = useCallback(
    (subtab: AssetSubtab) => {
      const params = new URLSearchParams(location.search);
      if (subtab === 'overview') {
        params.delete('subtab');
      } else {
        params.set('subtab', subtab);
      }
      const search = params.toString();
      history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
    },
    [history, location.pathname, location.search],
  );

  // Assets are scoped to the current level — no `includeChildren` toggle on this surface.
  const { data: selectedAsset, isLoading: isLoadingAsset } = useGetAssetQuery(
    { initiativeId, assetId },
    { skip: !initiativeId || !assetId },
  );
  const { data: comparisonData } = useGetAssetComparisonQuery(
    { initiativeId, periodStart: period.start, periodEnd: period.end },
    { skip: !initiativeId },
  );

  const selectedComparisonAsset = useMemo(
    () => comparisonData?.assets?.find((a) => a._id === assetId),
    [comparisonData?.assets, assetId],
  );

  const navigateBack = () => {
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ASSETS, { initiativeId }));
  };

  // Persist a freshly-picked activity for the asset. Mirrors the same handler
  // used inside `AssetDetailSection` so the chrome "Add Activity" button and
  // the in-card one create activities with identical payloads.
  const handlePersistActivity = useCallback(
    async (entry: AssetActivityEntry): Promise<{ activityId: string }> => {
      if (!selectedAsset) {
        throw new Error('selectedAsset is required');
      }
      const assetInitiativeId = selectedAsset.initiativeId || initiativeId;
      const { periodStart: ps, periodEnd: pe } = expandMonthToPeriodRange(entry.periodStart, entry.periodEnd);
      const data: CreateActivityRequest = {
        scope: entry.scope,
        activityType: entry.activityType,
        periodStart: ps,
        periodEnd: pe,
        quantity: Number(entry.quantity),
        unit: entry.unit as ActivityUnit,
        region: entry.region,
        regionFallback: entry.regionFallback,
        yearFallback: entry.yearFallback,
        assetId: selectedAsset._id,
        ...(entry.fuelType ? { fuelType: entry.fuelType as FuelType } : {}),
        ...(entry.vehicleType ? { vehicleType: entry.vehicleType as VehicleType } : {}),
        ...(entry.refrigerantType ? { refrigerantType: entry.refrigerantType as RefrigerantGasType } : {}),
        ...(entry.processType ? { processType: entry.processType as ProcessType } : {}),
        ...(entry.name ? { name: entry.name.trim() } : {}),
        ...(entry.dataSource ? { dataSource: entry.dataSource.trim() } : {}),
        ...(entry.notes ? { notes: entry.notes.trim() } : {}),
      };
      const result = await createActivity({ initiativeId: assetInitiativeId, data }).unwrap();
      return { activityId: result.data._id };
    },
    [createActivity, initiativeId, selectedAsset],
  );

  if (isLoadingAsset) {
    return (
      <Dashboard>
        <div className='tw:text-center tw:py-16 tw:text-muted-foreground'>
          <Loader2 className='tw:size-8 tw:animate-spin tw:mx-auto tw:mb-3' />
          Loading asset...
        </div>
      </Dashboard>
    );
  }

  if (!selectedAsset) {
    return (
      <Dashboard>
        <div className='tw:text-center tw:py-16 tw:text-muted-foreground'>
          <p>Asset not found.</p>
          <Button variant='link' onClick={navigateBack} className='tw:mt-4'>
            <ArrowLeft className='tw:size-4 tw:mr-2' />
            Back to Assets
          </Button>
        </div>
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      <div className='tw:space-y-6'>
        {/* Chrome — Back link + period selector */}
        <div className='tw:flex tw:justify-between tw:items-center tw:gap-3'>
          <Button variant='link' size='sm' onClick={navigateBack}>
            <ArrowLeft className='tw:size-4 tw:mr-1' />
            Back to Assets
          </Button>
          <FiscalYearSelector
            selectedYear={selectedFiscalYear}
            onYearChange={setSelectedFiscalYear}
            years={availableYears}
          />
        </div>

        {/* Asset header (title, type, metadata, Edit + Add Activity) — sits above the tabs. */}
        <AssetHeader
          asset={selectedAsset}
          actions={
            <>
              <Button variant='outline' size='sm' onClick={() => setIsOnePagerOpen(true)}>
                <Download className='tw:size-3.5 tw:mr-1' />
                Export
              </Button>
              <Button variant='outline' size='sm' onClick={() => setIsEditOpen(true)}>
                <Pencil className='tw:size-3.5 tw:mr-1' />
                Edit
              </Button>
              <Button variant='outline' size='sm' onClick={() => setIsDeleteOpen(true)}>
                <Trash2 className='tw:size-3.5 tw:mr-1 tw:text-destructive' />
                Delete
              </Button>
              <AddActivityToExistingAssetButton
                asset={selectedAsset}
                onPersistActivity={handlePersistActivity}
              />
            </>
          }
        />

        <TabNav variant='links' tabs={subtabs} activeTab={activeSubtab} onChange={setActiveSubtab} />

        {activeSubtab === 'overview' && (
          <AssetDetailSection
            initiativeId={initiativeId}
            periodStart={period.start}
            periodEnd={period.end}
            asset={selectedAsset}
            comparisonAsset={selectedComparisonAsset}
            allTimePeriodStart={allTimePeriod?.start}
            allTimePeriodEnd={allTimePeriod?.end}
          />
        )}

        {activeSubtab === 'data-quality' && (
          <AssetDataQualityTab
            initiativeId={initiativeId}
            asset={selectedAsset}
            periodStart={period.start}
            periodEnd={period.end}
            allTimePeriodStart={allTimePeriod?.start}
            allTimePeriodEnd={allTimePeriod?.end}
          />
        )}

        {COMING_SOON_SUBTABS.has(activeSubtab) && (
          <Card>
            <CardContent>
              <div className='tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-16 tw:text-center'>
                <div className='tw:rounded-full tw:bg-muted tw:p-4 tw:mb-4'>
                  {subtabs.find((t) => t.key === activeSubtab)?.icon}
                </div>
                <h5 className='tw:font-semibold tw:mb-2'>
                  {subtabs.find((t) => t.key === activeSubtab)?.label}
                </h5>
                <p className='tw:text-sm tw:text-muted-foreground tw:max-w-md tw:mb-0'>
                  This section is coming soon.
                </p>
                <span className='tw:text-xs tw:bg-muted tw:text-muted-foreground tw:px-3 tw:py-1 tw:rounded-full tw:mt-2'>
                  Coming Soon
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        <EditAssetModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          initiativeId={initiativeId}
          asset={selectedAsset}
        />

        <AssetOnePagerModal
          isOpen={isOnePagerOpen}
          toggle={() => setIsOnePagerOpen(false)}
          initiativeId={initiativeId}
          asset={selectedAsset}
        />

        <DeleteAssetModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onDeleted={() => {
            setIsDeleteOpen(false);
            navigateBack();
          }}
          initiativeId={initiativeId}
          asset={selectedAsset}
        />
      </div>
    </Dashboard>
  );
};
